/**
 * The month's events as a searchable table (POS2-1259 CP4).
 *
 * Search spans name, type, room, service style and notes — the spec's five fields. Type and
 * style are matched on their **translated** labels as well as their stored values, because a
 * German user typing "Hochzeit" is searching for what the screen shows them, not for `wedding`.
 */
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { DayCovers, OccupancyEvent } from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { orderedEvents, shortTime, TYPE_COLOURS } from "@/lib/occupancy";
import { SERVICE_STYLE_LABEL_KEYS, TYPE_LABEL_KEYS } from "@/lib/occupancy-labels";

type Row = { event: OccupancyEvent; dayTotal: number | null };

export function ListView({
  days,
  search,
  onSearchChange,
  onEditEvent,
  onDeleteEvent,
}: {
  days: DayCovers[];
  search: string;
  onSearchChange: (value: string) => void;
  /** Absent for a viewer without `occupancy:update` — decision 26: the affordance is missing,
   *  never present and refused. Both travel together, so the actions column goes with them. */
  onEditEvent?: (event: OccupancyEvent) => void;
  onDeleteEvent?: (event: OccupancyEvent) => void;
}) {
  const { t } = useTranslation();
  const canWrite = onEditEvent !== undefined || onDeleteEvent !== undefined;

  const rows = useMemo<Row[]>(() => {
    const all: Row[] = [];
    for (const day of days) {
      for (const event of orderedEvents(day.events)) {
        all.push({ event, dayTotal: day.total });
      }
    }
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(({ event }) =>
      [
        event.name,
        event.room ?? "",
        event.notes ?? "",
        event.event_type,
        t(TYPE_LABEL_KEYS[event.event_type]),
        event.service_style ?? "",
        event.service_style ? t(SERVICE_STYLE_LABEL_KEYS[event.service_style]) : "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [days, search, t]);

  return (
    <div className="flex flex-col gap-3">
      <Input
        value={search}
        aria-label={t("occupancy.list.search")}
        placeholder={t("occupancy.list.searchPlaceholder")}
        className="max-w-sm"
        onChange={(e) => onSearchChange(e.target.value)}
      />

      <div className="overflow-x-auto rounded-xl border border-border bg-background">
        <table className="w-full text-[13px]">
          <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-semibold">{t("occupancy.list.colDate")}</th>
              <th className="px-3 py-2 font-semibold">{t("occupancy.list.colEvent")}</th>
              <th className="px-3 py-2 font-semibold">{t("occupancy.list.colType")}</th>
              <th className="px-3 py-2 font-semibold">{t("occupancy.list.colRoom")}</th>
              <th className="px-3 py-2 text-right font-semibold">
                {t("occupancy.list.colCovers")}
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                {t("occupancy.list.colDayTotal")}
              </th>
              {canWrite && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={canWrite ? 7 : 6}
                  className="px-3 py-10 text-center text-muted-foreground"
                >
                  {t("occupancy.list.empty")}
                </td>
              </tr>
            ) : (
              rows.map(({ event, dayTotal }) => {
                const start = shortTime(event.start_time);
                const end = shortTime(event.end_time);
                return (
                  <tr key={event.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 tabular-nums">
                      <div>{event.date}</div>
                      <div className="text-xs text-muted-foreground">
                        {start && end ? `${start}–${end}` : (start ?? "")}
                      </div>
                    </td>
                    <td className="max-w-56 px-3 py-2">
                      <div className="truncate font-medium">{event.name}</div>
                      {event.service_style && (
                        <div className="text-xs text-muted-foreground">
                          {t(SERVICE_STYLE_LABEL_KEYS[event.service_style])}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="size-2 rounded-[2px]"
                          style={{ background: TYPE_COLOURS[event.event_type] }}
                        />
                        {t(TYPE_LABEL_KEYS[event.event_type])}
                      </span>
                    </td>
                    <td className="max-w-40 truncate px-3 py-2 text-muted-foreground">
                      {event.room ?? ""}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{event.covers}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{dayTotal ?? "—"}</td>
                    {canWrite && (
                      <td className="px-3 py-2 text-right">
                        {onEditEvent && (
                          <Button variant="ghost" size="sm" onClick={() => onEditEvent(event)}>
                            {t("occupancy.action.edit")}
                          </Button>
                        )}
                        {onDeleteEvent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => onDeleteEvent(event)}
                          >
                            {t("occupancy.action.delete")}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
